// Borotok client: one module, History API router, DOM built with textContent only.
import { startG3Telemetry } from './g3-telemetry/index.js';
import { startInstallPrompt } from './install.js';

// the "Report bug" button of previews; with G3_INGEST_URL empty it renders nothing
startG3Telemetry({ ingestUrl: G3_INGEST_URL, reportToken: G3_REPORT_TOKEN, commit: G3_COMMIT_SHA, deployUrl: G3_DEPLOY_URL });
const app = document.getElementById('app');
const nav = document.getElementById('nav');
let state = { user: null };
let cleanup = () => {};
let saving = Promise.resolve(); // settings writes, in order; the feed waits for them

const PUBLIC = /^\/(login|setup|join\/[^/]+|reset\/[^/]+)$/;
const siteName = id => state.sites?.find(s => s.id === id)?.name ?? id;

// ---------- helpers ----------
export function el(tag, attrs = {}, ...kids) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith('on')) e.addEventListener(k.slice(2), v);
    else if (k === 'text') e.textContent = v;
    else if (k in e && k !== 'list' && k !== 'type') e[k] = v;
    else e.setAttribute(k, v === true ? '' : v);
  }
  for (const c of kids.flat()) if (c !== null && c !== undefined && c !== false) e.append(c);
  return e;
}

// stroke icons on a 24 × 24 grid, drawn in the text colour
const ICONS = {
  flame: 'M12 21.5c-3.9 0-6.5-2.7-6.5-6.3 0-3.6 2.8-5.6 3.9-9.4.2-.8 1.1-1 1.6-.4 2.6 2.9 7.5 6.2 7.5 9.9 0 3.5-2.6 6.2-6.5 6.2ZM12 21.5c-1.6 0-2.8-1.2-2.8-2.8 0-2 1.7-3 2.8-4.8 1.1 1.8 2.8 2.8 2.8 4.8 0 1.6-1.2 2.8-2.8 2.8Z',
  search: 'M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 1 0 0-13ZM15.3 15.3 20 20',
  star: 'M12 3.2 14.5 9.3 21.1 9.8 16.1 14.1 17.6 20.6 12 17.1 6.4 20.6 7.9 14.1 2.9 9.8 9.5 9.3Z',
  sliders: 'M4 7h9M17 7h3M15 5v4M4 17h3M11 17h9M9 15v4',
  heart: 'M12 20.2 4.9 13.1a4.5 4.5 0 0 1 6.4-6.4l.7.7.7-.7a4.5 4.5 0 0 1 6.4 6.4Z',
  bubble: 'M20 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.2A8 8 0 1 1 20 12Z',
  flag: 'M5 21V4h11l-2 4 2 4H5',
  eye: 'M2.5 12C4.6 8 8 5.5 12 5.5s7.4 2.5 9.5 6.5c-2.1 4-5.5 6.5-9.5 6.5S4.6 16 2.5 12ZM12 9a3 3 0 1 0 0 6 3 3 0 1 0 0-6Z',
};
export function icon(name) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'i');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', ICONS[name]);
  svg.append(path);
  return svg;
}

export async function api(method, path, body, { keepalive = false } = {}) {
  const opts = { method, headers: {}, keepalive };
  if (method !== 'GET') { opts.headers['content-type'] = 'application/json'; opts.body = JSON.stringify(body ?? {}); }
  const r = await fetch(path, opts);
  let data = {};
  try { data = await r.json(); } catch { /* empty body */ }
  if (r.status === 401 && !PUBLIC.test(location.pathname)) {
    state = { user: null }; navigate('/login');
    throw Object.assign(new Error(''), { status: 401 }); // empty message: the login screen says enough
  }
  if (!r.ok) throw Object.assign(new Error(data.error || 'Something went wrong - try again'), { status: r.status });
  return data;
}

export function navigate(path, replace = false) {
  history[replace ? 'replaceState' : 'pushState'](null, '', path);
  render();
}

let toastTimer;
export function toast(text) {
  if (!text) return;
  const t = document.getElementById('toast');
  t.textContent = text; t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { t.hidden = true; }, 2500);
}

async function loadState() { state = await api('GET', '/api/state'); }

function setBadge() {
  const b = nav.querySelector('.badge'), n = state.waitingReports || 0;
  b.textContent = n; b.hidden = !(state.user?.role === 'admin' && n > 0);
}

// ---------- router ----------
export function render() {
  cleanup(); cleanup = () => {};
  document.getElementById('comments')?.remove();
  const path = location.pathname.replace(/(.)\/$/, '$1');
  if (state.needsSetup && path !== '/setup') return navigate('/setup', true);
  if (!state.needsSetup && !state.user && !PUBLIC.test(path)) return navigate('/login', true);
  if (state.user && (path === '/login' || path === '/setup')) return navigate('/', true);
  const signedIn = Boolean(state.user) && !PUBLIC.test(path);
  nav.hidden = !signedIn;
  document.body.classList.toggle('signed-in', signedIn);
  for (const a of nav.querySelectorAll('a')) a.classList.toggle('on', a.dataset.nav === '/' ? path === '/' : path.startsWith(a.dataset.nav));
  setBadge();
  app.replaceChildren();
  window.scrollTo(0, 0);
  let m;
  if (path === '/') return feedScreen();
  if (path === '/login') return loginScreen();
  if (path === '/setup') return setupScreen();
  if ((m = /^\/join\/([^/]+)$/.exec(path))) return joinScreen(decodeURIComponent(m[1]));
  if (path === '/settings') return settingsScreen();
  if (path === '/members') return state.user.role === 'admin' ? membersScreen() : navigate('/', true);
  if ((m = /^\/reset\/([^/]+)$/.exec(path))) return resetScreen(decodeURIComponent(m[1]));
  if (path === '/search') return searchScreen();
  if ((m = /^\/poster\/([^/]+)\/([^/]+)$/.exec(path))) return posterScreen(decodeURIComponent(m[1]), decodeURIComponent(m[2]));
  if (path === '/favourites') return gridScreen({ heading: 'Favourites', loadPage: async () => (await api('GET', '/api/favourites')).posts, emptyText: 'Nothing saved yet.' });
  if (path === '/reports') return state.user.role === 'admin' ? reportsScreen() : navigate('/', true);
  return lostScreen();
}

addEventListener('popstate', render);
document.addEventListener('click', e => {
  const a = e.target.closest('a[href^="/"]');
  if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || a.target) return;
  e.preventDefault();
  if (a.getAttribute('href') !== location.pathname + location.search) navigate(a.getAttribute('href'));
});

// ---------- sign-in style screens ----------
function brand(...kids) {
  return el('section', { className: 'sign' },
    el('img', { src: '/logo.jpg', alt: 'Borotok logo', className: 'logo' }),
    el('h1', { className: 'name', text: 'Borotok' }), ...kids);
}
let fieldN = 0;
function field(label, attrs) {
  const id = 'f' + fieldN++;
  return el('div', { className: 'field' }, el('label', { htmlFor: id, text: label }), el('input', { id, ...attrs }));
}
// a form that posts its fields to `path`; on success reloads state and opens the feed
function authForm({ heading, button, path, extra = {}, fields, after }) {
  const error = el('p', { className: 'error', role: 'alert' });
  const btn = el('button', { type: 'submit', className: 'primary', text: button });
  const form = el('form', {
    onsubmit: async e => {
      e.preventDefault();
      error.textContent = ''; btn.disabled = true;
      const body = { ...extra };
      for (const i of form.querySelectorAll('input')) body[i.name] = i.value;
      try {
        await api('POST', path, body);
        await loadState();
        navigate('/', true);
      } catch (err) { error.textContent = err.message; btn.disabled = false; }
    },
  }, heading && el('h2', { text: heading }), fields, error, btn, after);
  return form;
}
const userField = () => field('Username', { name: 'username', autocomplete: 'username', required: true, autocapitalize: 'none', spellcheck: false });
const passField = (auto = 'current-password') => field('Password', { name: 'password', type: 'password', autocomplete: auto, required: true });

function loginScreen() {
  app.append(brand(authForm({
    button: 'Sign in', path: '/api/login', fields: [userField(), passField()],
    after: el('p', { className: 'small', text: 'Forgot your password? Ask the admin for a reset link.' }),
  })));
}
function setupScreen() {
  app.append(brand(authForm({
    heading: 'Set up Borotok', button: 'Create admin', path: '/api/setup',
    fields: [el('p', { className: 'small', text: 'First run - pick the admin account.' }), userField(), passField('new-password')],
  })));
}
async function joinScreen(token) {
  const box = brand(el('p', { className: 'placeholder', text: 'Checking your invite…' }));
  app.append(box);
  let valid = false;
  try { valid = (await api('GET', '/api/invite/' + encodeURIComponent(token))).valid; } catch { /* treat as invalid */ }
  box.lastChild.remove();
  if (!valid) return box.append(el('p', { className: 'error', text: 'This invite has already been used or is no longer valid - ask for a new one' }));
  box.append(authForm({
    heading: 'Join Borotok', button: 'Join', path: '/api/join', extra: { token },
    fields: [userField(), passField('new-password')],
  }));
}
async function resetScreen(token) {
  const box = brand(el('p', { className: 'placeholder', text: 'Checking your link…' }));
  app.append(box);
  let valid = false;
  try { valid = (await api('GET', '/api/reset/' + encodeURIComponent(token))).valid; } catch { /* treat as invalid */ }
  box.lastChild.remove();
  if (!valid) return box.append(el('p', { className: 'error', text: 'This link is no longer valid - ask for a new one' }));
  box.append(authForm({
    heading: 'Set a new password', button: 'Save password', path: '/api/reset', extra: { token },
    fields: [field('New password', { name: 'password', type: 'password', autocomplete: 'new-password', required: true, minLength: 8 })],
  }));
}
function lostScreen() {
  app.append(el('section', { className: 'page' }, el('h1', { text: 'Nothing here' }), el('a', { href: '/', text: 'Back to the feed' })));
}

// ---------- feed ----------
function feedScreen() {
  renderFeed(app, async page => { await saving; return api('GET', `/api/feed?page=${page}`); });
}

export let openComments = (p, a) => commentsPanel(p, a);
export const setOpenComments = fn => { openComments = fn; };

const fmt = n => (n >= 10000 ? Math.round(n / 1000) + 'k' : String(n));
const postKey = p => `${p.site}:${p.id}`;
const counted = new Set(); // posts whose view was counted in this page load
let pageHidden = () => {}, pageShown = () => {}; // set by the feed on screen
const onHide = () => pageHidden();
addEventListener('pagehide', onHide);
document.addEventListener('visibilitychange', () => (document.hidden ? onHide() : pageShown()));

// loadPage(n) → {posts, down, done?}; pass `done: true` (e.g. a fixed list) to stop paging.
// opts: startIndex (scroll to that post first), sites (for the "all down" check), empty(allDown) → node
export function renderFeed(container, loadPage, opts = {}) {
  const sites = opts.sites ?? state.settings?.sites ?? [];
  const placeholder = el('div', { className: 'placeholder', text: 'Warming up your feed…' });
  container.append(placeholder);
  const feed = el('div', { id: 'feed' });
  const seen = new Set(), items = [];
  let page = 0, emptyRun = 0, loading = false, finished = false, current = 0, alive = true, started = false;

  const watching = new Map(); // article → {since, timer} while ≥60 % visible
  function start(a) {
    const s = { since: Date.now() };
    s.timer = setTimeout(async () => {
      if (counted.has(postKey(a.post))) return;
      counted.add(postKey(a.post));
      try { a.querySelector('.views').textContent = (await api('POST', '/api/view', { post: a.post, ms: 0 })).views; } catch { /* not important */ }
    }, 1000);
    watching.set(a, s);
  }
  // sends the watch time of a post that leaves the screen (keepalive when the page is going away)
  function leave(a, keepalive = false) {
    const s = watching.get(a);
    if (!s) return;
    watching.delete(a);
    clearTimeout(s.timer);
    const ms = Date.now() - s.since;
    if (counted.has(postKey(a.post)) && ms > 0) api('POST', '/api/view', { post: a.post, ms, more: true }, { keepalive }).catch(() => {});
  }
  const io = new IntersectionObserver(entries => {
    for (const e of entries) {
      const a = e.target, v = a.querySelector('video');
      if (e.isIntersecting && e.intersectionRatio >= 0.6) {
        current = items.indexOf(a);
        if (v && !document.hidden) v.play().catch(() => {});
        if (!watching.has(a)) start(a);
        maybeMore();
      } else {
        if (v) v.pause();
        leave(a);
      }
    }
  }, { root: feed, threshold: [0, 0.6] });

  function maybeMore() {
    if (!finished && !loading && current >= items.length - 5) more();
  }

  async function more() {
    loading = true;
    let res;
    try { res = await loadPage(page); } catch (e) {
      loading = false;
      if (!alive || e.status === 401) return;
      // a failed load is not an empty page: say so and let them try the same page again
      if (!started) {
        placeholder.replaceChildren(el('p', { text: "Couldn't load posts - check your connection." }),
          el('button', { type: 'button', className: 'primary', text: 'Retry', onclick: () => { placeholder.textContent = 'Warming up your feed…'; more(); } }));
      } else {
        toast("Couldn't load more posts - scroll to try again");
      }
      return;
    }
    if (!alive) return;
    page++;
    let added = 0;
    for (const p of res.posts ?? []) {
      if (seen.has(postKey(p))) continue;
      seen.add(postKey(p));
      const a = postEl(p);
      items.push(a); feed.append(a); io.observe(a); added++;
    }
    const allDown = sites.length > 0 && sites.every(s => (res.down ?? []).includes(s));
    emptyRun = added ? 0 : emptyRun + 1;
    if (res.done || allDown || emptyRun >= 3) finished = true;
    if (!started && (items.length || finished)) {
      started = true;
      if (!items.length) feed.append((opts.empty ?? emptyNode)(allDown));
      placeholder.replaceWith(feed);
      if (opts.startIndex && items[opts.startIndex]) items[opts.startIndex].scrollIntoView({ block: 'start' });
    }
    loading = false;
    if (!started) { if (!finished) more(); return; }
    maybeMore();
  }

  function remove(a) {
    io.unobserve(a); leave(a);
    items.splice(items.indexOf(a), 1);
    a.remove();
    maybeMore();
  }

  function postEl(p) {
    const a = el('article', { className: 'post' });
    a.post = p;
    Object.assign(a.dataset, { site: p.site, id: p.id, media: p.media, tags: p.tags.join(' ') });
    const media = mediaEl(p, { muted: true, loop: true, onclick: e => { e.currentTarget.muted = !e.currentTarget.muted; } });
    const count = n => el('span', { className: 'count', text: fmt(n) });
    const like = el('button', { className: 'like', 'aria-pressed': String(p.liked), 'aria-label': 'Like' },
      el('span', { className: 'icon', 'aria-hidden': 'true' }, icon('heart')), count(p.likes));
    like.addEventListener('click', async () => {
      try {
        const r = await api('POST', '/api/like', { post: p });
        p.liked = r.liked; p.likes = r.likes;
        like.setAttribute('aria-pressed', String(r.liked));
        like.querySelector('.count').textContent = fmt(r.likes);
      } catch (e) { toast(e.message); }
    });
    const save = el('button', { className: 'save', 'aria-pressed': String(p.saved) },
      el('span', { className: 'icon', 'aria-hidden': 'true' }, icon('star')), el('span', { className: 'label', text: 'Save' }));
    save.addEventListener('click', async () => {
      try {
        const r = await api('POST', '/api/favourite', { post: p });
        p.saved = r.saved;
        save.setAttribute('aria-pressed', String(r.saved));
      } catch (e) { toast(e.message); }
    });
    const report = el('button', { className: 'report' },
      el('span', { className: 'icon', 'aria-hidden': 'true' }, icon('flag')), el('span', { className: 'label', text: 'Report' }));
    report.addEventListener('click', async () => {
      try { await api('POST', '/api/report', { post: p }); remove(a); toast('Reported - thanks'); } catch (e) { toast(e.message); }
    });
    const comments = el('button', { className: 'comments', 'aria-label': 'Comments', onclick: () => openComments(p, a) },
      el('span', { className: 'icon', 'aria-hidden': 'true' }, icon('bubble')), count(p.comments),
      p.siteComments !== null && p.siteComments !== undefined && el('span', { className: 'site-count', text: `site: ${p.siteComments}` }));
    const tagLinks = p.tags.map((t, i) => el('a', { className: 'tag', href: `/search?tags=${encodeURIComponent(t)}`, text: t, hidden: i >= 4 }));
    const extra = p.tags.length - 4;
    const toggle = extra > 0 && el('button', { type: 'button', className: 'tag toggle', 'aria-expanded': 'false', text: `+${extra}` });
    if (toggle) toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? 'see less' : `+${extra}`;
      tagLinks.slice(4).forEach(t => { t.hidden = !open; });
    });
    a.append(
      media,
      el('div', { className: 'top' }, el('span', { className: 'eye', 'aria-hidden': 'true' }, icon('eye')), el('span', { className: 'views', text: String(p.views) })),
      el('div', { className: 'side' },
        el('div', { className: 'act' }, like, p.score !== null && p.score !== undefined && el('span', { className: 'site-score', text: `site: ${p.score}` })),
        el('div', { className: 'act' }, comments), el('div', { className: 'act' }, save), el('div', { className: 'act' }, report)),
      el('div', { className: 'info' },
        p.poster && el('a', { className: 'poster', href: `/poster/${encodeURIComponent(p.site)}/${encodeURIComponent(p.poster)}`, text: `${p.poster} · ${siteName(p.site)}` }),
        el('div', { className: 'tags' }, tagLinks, toggle)),
    );
    return a;
  }

  pageHidden = () => {
    for (const a of items) a.querySelector('video')?.pause();
    for (const a of [...watching.keys()]) leave(a, true);
  };
  pageShown = () => {
    const a = items[current];
    if (!a || a.getBoundingClientRect().height === 0) return;
    a.querySelector('video')?.play().catch(() => {});
    if (!watching.has(a)) start(a);
  };
  cleanup = () => {
    alive = false;
    for (const a of [...watching.keys()]) leave(a);
    io.disconnect();
    pageHidden = pageShown = () => {};
  };
  more();
  return { feed, items };
}

function emptyNode(allDown) {
  return el('div', { className: 'empty' },
    el('p', { text: allDown ? 'None of your sites answered right now.' : 'Nothing matches your settings.' }),
    el('a', { href: '/settings', text: 'Settings > Sites' }));
}

// ---------- comments ----------
function commentsPanel(p, article) {
  document.getElementById('comments')?.remove();
  const list = el('div', { className: 'list' }, el('p', { className: 'muted', text: 'Loading…' }));
  const send = el('button', { type: 'submit', className: 'primary', text: 'Post' });
  const input = el('textarea', { 'aria-label': 'Write a comment', placeholder: 'Write a comment…', maxLength: 2000, rows: 2 });
  const form = el('form', {
    className: 'write',
    onsubmit: async e => {
      e.preventDefault();
      const body = input.value.trim();
      if (!body || send.disabled) return;
      send.disabled = true;
      try { await api('POST', '/api/comments', { post: p, body }); input.value = ''; load(); } catch (err) { toast(err.message); }
      send.disabled = false;
    },
  }, input, send);
  const panel = el('section', { id: 'comments', role: 'dialog', 'aria-label': 'Comments' },
    el('header', {}, el('h2', { text: 'Comments' }), el('button', { type: 'button', className: 'close', text: 'Close', onclick: () => panel.remove() })),
    list, form);
  document.body.append(panel);

  const when = t => new Date(t).toLocaleString();
  function comment(c, canDeleteAll) {
    const body = el('p', { className: 'body', text: c.body });
    const edit = () => {
      const area = el('textarea', { 'aria-label': 'Edit comment', text: c.body, maxLength: 2000, rows: 3 });
      const save = el('button', { type: 'submit', className: 'primary', text: 'Save' });
      body.replaceWith(el('form', {
        className: 'edit',
        onsubmit: async e => {
          e.preventDefault();
          if (save.disabled) return;
          save.disabled = true;
          try { await api('PUT', `/api/comments/${c.id}`, { body: area.value }); load(); } catch (err) { toast(err.message); save.disabled = false; }
        },
      }, area, el('div', { className: 'acts' },
        save,
        el('button', { type: 'button', text: 'Cancel', onclick: () => load() }))));
      area.focus();
    };
    const del = async () => {
      if (!confirm('Delete this comment and all its versions?')) return;
      try { await api('DELETE', `/api/comments/${c.id}`, {}); load(); } catch (err) { toast(err.message); }
    };
    return el('div', { className: 'comment', 'data-id': c.id },
      el('div', { className: 'meta' }, el('span', { className: 'author', text: c.author }), el('span', { className: 'date', text: when(c.date) })),
      body,
      c.versions.length > 0 && el('details', { className: 'versions' }, el('summary', { text: 'edited - show earlier versions' }),
        c.versions.map(v => el('div', { className: 'old' }, el('p', { className: 'old-body', text: v.body }), el('span', { className: 'old-date', text: when(v.date) })))),
      (c.mine || canDeleteAll) && el('div', { className: 'acts' },
        c.mine && el('button', { type: 'button', text: 'Edit', onclick: edit }),
        el('button', { type: 'button', className: 'danger', text: 'Delete', onclick: del })));
  }
  async function load() {
    let r;
    try { r = await api('GET', `/api/comments?site=${encodeURIComponent(p.site)}&id=${encodeURIComponent(p.id)}`); } catch (e) {
      list.replaceChildren(el('p', { className: 'muted', text: "Couldn't load comments - try again." }));
      return toast(e.message);
    }
    p.comments = r.comments.length;
    const n = article?.querySelector('button.comments .count');
    if (n) n.textContent = fmt(p.comments);
    list.replaceChildren(...[
      r.comments.length ? r.comments.map(c => comment(c, r.canDeleteAll)) : el('p', { className: 'muted', text: 'No comments here yet - say hi.' }),
      ...r.siteComments.map(c => el('div', { className: 'site-comment' },
        el('div', { className: 'meta' }, el('span', { className: 'author', text: c.author }), el('span', { className: 'from', text: `from ${siteName(p.site)}` }),
          el('span', { className: 'date', text: c.date })),
        el('p', { className: 'body', text: c.body }))),
    ].flat());
  }
  load();
}

// ---------- search, posters, grids ----------
let searchMode = 'tags';
function searchScreen() {
  const q = new URLSearchParams(location.search).get('tags');
  if (q) {
    return renderFeed(app, async page => { await saving; return api('GET', `/api/search?tags=${encodeURIComponent(q)}&page=${page}`); },
      { empty: allDown => (allDown ? emptyNode(true) : el('div', { className: 'empty', text: 'Nothing found - try removing a tag' })) });
  }
  const input = el('input', { type: 'search', 'aria-label': 'Search', autocapitalize: 'none', autocomplete: 'off', spellcheck: false });
  const results = el('div', { className: 'results' });
  const chips = el('ul', { className: 'chips' });
  const picks = new Set();
  const addTag = t => {
    if (!t || picks.has(t)) return;
    picks.add(t);
    const li = el('li', { className: 'chip' }, el('span', { text: t }),
      el('button', { type: 'button', 'aria-label': `Remove ${t}`, text: '×', onclick: () => { picks.delete(t); li.remove(); input.focus(); } }));
    chips.append(li);
  };
  const suggest = el('ul', { className: 'suggest', role: 'listbox', 'aria-label': 'Suggestions', hidden: true });
  let askId = 0;
  const showSuggestions = async () => {
    const term = input.value.trim().toLowerCase(), id = ++askId;
    suggest.replaceChildren(); suggest.hidden = true;
    if (searchMode !== 'tags' || !term) return;
    let tags;
    try { tags = (await api('GET', `/api/tags?q=${encodeURIComponent(term)}`)).tags; } catch { return; }
    if (!alive || id !== askId) return;
    suggest.replaceChildren(...tags.filter(t => !picks.has(t)).map(t => el('li', { role: 'option' }, el('button', {
      type: 'button', text: t,
      onclick: () => { addTag(t); input.value = ''; showSuggestions(); input.focus(); },
    }))));
    suggest.hidden = !suggest.children.length;
  };
  let timer;
  input.addEventListener('input', () => {
    if (searchMode === 'tags' && /\s/.test(input.value)) {
      const words = input.value.trim().split(/\s+/);
      const rest = /\s$/.test(input.value) ? '' : words.pop();
      words.forEach(w => addTag(w.toLowerCase()));
      input.value = rest ?? '';
    }
    clearTimeout(timer);
    timer = setTimeout(showSuggestions, 200);
  });
  const toggle = mode => el('button', {
    type: 'button', text: mode === 'tags' ? 'Tags' : 'Posters', 'aria-pressed': String(searchMode === mode),
    onclick: () => { searchMode = mode; for (const b of modes.children) b.setAttribute('aria-pressed', String(b === modes.children[mode === 'tags' ? 0 : 1])); hint(); results.replaceChildren(); showSuggestions(); },
  });
  const modes = el('div', { className: 'modes' }, toggle('tags'), toggle('posters'));
  const hint = () => { input.placeholder = searchMode === 'tags' ? 'tags, e.g. cat red' : 'exact poster name'; chips.hidden = searchMode !== 'tags'; };
  hint();
  let alive = true;
  cleanup = () => { alive = false; clearTimeout(timer); };
  const form = el('form', {
    className: 'search',
    onsubmit: async e => {
      e.preventDefault();
      const text = input.value.trim();
      if (searchMode === 'tags') {
        text.toLowerCase().split(/\s+/).forEach(addTag);
        if (!picks.size) return;
        return navigate('/search?tags=' + encodeURIComponent([...picks].join(' ')));
      }
      if (!text) return;
      results.replaceChildren(el('p', { className: 'muted', text: 'Looking…' }));
      let posts;
      try { posts = (await api('GET', `/api/poster?name=${encodeURIComponent(text)}`)).posts; } catch (err) { results.replaceChildren(); return toast(err.message); }
      if (!alive) return;
      const by = new Map();
      for (const p of posts) if (p.poster && !by.has(p.site)) by.set(p.site, p.poster);
      results.replaceChildren(...(by.size
        ? [...by].map(([site, name]) => el('a', { className: 'poster-row', href: `/poster/${encodeURIComponent(site)}/${encodeURIComponent(name)}`, text: `${name} · ${siteName(site)}` }))
        : [el('div', { className: 'empty', text: 'Nothing found - check the name' })]));
    },
  }, modes, chips, el('div', { className: 'row' }, input, el('button', { type: 'submit', className: 'primary', text: 'Search' })), suggest);
  app.append(el('section', { className: 'page' }, el('h1', { text: 'Search' }), form, results));
  input.focus();
}

function posterScreen(site, name) {
  gridScreen({
    heading: `${name} · ${siteName(site)}`, paged: true, emptyText: 'Nothing here right now.',
    loadPage: async page => (await api('GET', `/api/poster?name=${encodeURIComponent(name)}&site=${encodeURIComponent(site)}&page=${page}`)).posts,
  });
}

// a grid of previews; `?at=i` opens the same list in the full-screen feed at post i
let lastGrid = null; // {key, posts} of the grid on screen, so the feed opens what was loaded
function gridScreen({ heading, loadPage, paged = false, emptyText }) {
  const key = location.pathname;
  const at = new URLSearchParams(location.search).get('at');
  if (at !== null) {
    const known = lastGrid?.key === key ? lastGrid.posts : null;
    return renderFeed(app, async () => ({ posts: known ?? await loadPage(0), done: true }),
      { startIndex: Math.max(0, Number(at) || 0), empty: () => el('div', { className: 'empty', text: emptyText }) });
  }
  const posts = [], seen = new Set();
  lastGrid = { key, posts };
  const grid = el('div', { className: 'grid' });
  const status = el('p', { className: 'muted', text: 'Loading…' });
  const more = paged && el('button', { type: 'button', className: 'more', text: 'More', hidden: true, onclick: () => next() });
  app.append(el('section', { className: 'page' }, el('h1', { text: heading }), status, grid, more));
  let page = 0, alive = true;
  cleanup = () => { alive = false; };
  async function next() {
    if (more) more.disabled = true;
    let got;
    try { got = await loadPage(page); } catch (e) {
      if (!alive) return;
      status.textContent = "Couldn't load - check your connection.";
      if (more) { more.disabled = false; more.hidden = page === 0; }
      return toast(e.message);
    }
    if (!alive) return;
    page++;
    for (const p of got) {
      if (seen.has(postKey(p))) continue;
      seen.add(postKey(p));
      grid.append(el('a', { className: 'tile', href: `${key}?at=${posts.length}`, 'data-site': p.site, 'data-id': p.id, 'data-media': p.media, 'aria-label': `Open post ${p.id}` },
        thumbEl(p)));
      posts.push(p);
    }
    if (posts.length) status.remove();
    else status.replaceWith(el('div', { className: 'empty', text: emptyText }));
    if (more) { more.disabled = false; more.hidden = !got.length; }
  }
  next();
}
const thumb = p => p.preview || (p.media === 'image' ? p.file : '');
// a preview that falls back to the full image, then to a "No preview" box instead of a broken-image icon
function thumbEl(p, className) {
  const src = thumb(p);
  const img = el('img', { className, src: src || undefined, alt: '', loading: 'lazy', referrerpolicy: 'no-referrer', 'data-g3-mask': true,
    onerror: () => {
      if (p.media === 'image' && img.src !== p.file) img.src = p.file;
      else img.parentElement?.classList.add('broken');
    } });
  if (!src) queueMicrotask(() => img.parentElement?.classList.add('broken'));
  return img;
}
// a post's full image or video, wrapped so a bug report's screenshot never shows it
function mediaEl(p, videoAttrs = {}) {
  const m = p.media === 'video'
    ? el('video', { src: p.file, poster: p.preview || undefined, playsInline: true, preload: 'metadata', referrerpolicy: 'no-referrer', ...videoAttrs })
    : el('img', { src: p.file, alt: '', loading: 'lazy', referrerpolicy: 'no-referrer' });
  if (p.media === 'video') { m.setAttribute('playsinline', ''); if (videoAttrs.muted) m.setAttribute('muted', ''); }
  const box = el('div', { className: 'media', 'data-g3-mask': true }, m);
  m.addEventListener('error', () => box.classList.add('broken'));
  return box;
}

// ---------- reported posts ----------
async function reportsScreen() {
  const list = el('ul', { className: 'reports' });
  const none = () => el('div', { className: 'empty', text: 'Nothing waiting - all clear.' });
  app.append(el('section', { className: 'page' }, el('h1', { text: 'Reported posts' }),
    el('p', { className: 'muted', text: 'Hidden from everyone until you choose.' }), list));
  let posts;
  try { posts = (await api('GET', '/api/reports')).posts; } catch (e) { return toast(e.message); }
  state.waitingReports = posts.length; setBadge();
  if (!posts.length) return list.replaceWith(none());
  for (const p of posts) {
    const decide = status => async () => {
      try {
        await api('POST', `/api/reports/${encodeURIComponent(p.site)}/${encodeURIComponent(p.id)}`, { status });
        li.remove();
        state.waitingReports = Math.max(0, (state.waitingReports || 0) - 1); setBadge();
        if (!list.children.length) list.replaceWith(none());
      } catch (e) { toast(e.message); }
    };
    const acts = () => el('div', { className: 'acts' },
      el('button', { type: 'button', className: 'primary', text: 'Restore', onclick: decide('restored') }),
      el('button', { type: 'button', className: 'danger', text: 'Keep removed', onclick: decide('removed') }));
    const li = el('li', { className: 'report', 'data-site': p.site, 'data-id': p.id },
      el('button', { type: 'button', className: 'open', 'aria-label': 'Open in full', onclick: () => openReported(p, acts) },
        thumbEl(p, 'thumb')),
      el('div', { className: 'what' },
        el('span', { text: `${p.poster || 'unknown'} · ${siteName(p.site)}` }),
        el('span', { className: 'muted small', text: p.tags.slice(0, 6).join(' ') }),
        el('button', { type: 'button', className: 'link', text: 'Open in full', onclick: () => openReported(p, acts) }),
        acts()));
    list.append(li);
  }
}

// the reported post in full (image or playable video, every tag), with the same two choices
function openReported(p, acts) {
  const buttons = acts();
  buttons.append(el('button', { type: 'button', text: 'Close' }));
  buttons.addEventListener('click', e => { if (e.target.closest('button')) box.close(); });
  const box = el('dialog', { className: 'reported', 'aria-label': 'Reported post', onclose: () => box.remove() },
    mediaEl(p, { controls: true }),
    el('div', { className: 'what' },
      el('span', { text: `${p.poster || 'unknown'} · ${siteName(p.site)}` }),
      el('span', { className: 'muted small', text: p.tags.join(' ') }),
      buttons));
  document.body.append(box);
  box.showModal();
}

// ---------- settings ----------
// runs fn after earlier settings writes; the feed waits for the queue, a failure doesn't block it
function queue(fn) {
  const p = saving.then(fn);
  saving = p.catch(() => {});
  return p;
}
function save(body) {
  return queue(() => api('PUT', '/api/settings', body))
    .then(s => { Object.assign(state.settings, { sites: s.sites, media: s.media }); })
    .catch(e => toast(e.message));
}

function settingsScreen() {
  const s = state.settings;
  const sitesBox = el('fieldset', { className: 'chips' }, el('legend', { text: 'Sites' }),
    state.sites.map(site => {
      const box = el('input', { type: 'checkbox', value: site.id, checked: s.sites.includes(site.id) });
      box.addEventListener('change', () => {
        const picked = [...sitesBox.querySelectorAll('input:checked')].map(i => i.value);
        if (!picked.length) { box.checked = true; return toast('Keep at least one site'); }
        save({ sites: picked });
      });
      return el('label', { className: 'chip' }, box, el('span', { text: site.name }));
    }));
  const mediaBox = el('fieldset', {}, el('legend', { text: 'Show' }),
    el('div', { className: 'seg' }, [['image', 'Images'], ['video', 'Videos'], ['both', 'Both']].map(([v, label]) =>
      el('label', { className: 'chip' },
        el('input', { type: 'radio', name: 'media', value: v, checked: s.media === v, onchange: () => save({ media: v }) }),
        el('span', { text: label })))));

  const list = el('ul', { className: 'hidden-tags' });
  const showTags = tags => {
    s.hiddenTags = tags;
    list.replaceChildren(...tags.map(t => el('li', {}, el('span', { text: t }),
      el('button', {
        type: 'button', text: 'Remove', 'aria-label': `Remove ${t}`,
        onclick: async () => { try { showTags((await queue(() => api('DELETE', '/api/hidden-tags', { tag: t }))).hiddenTags); } catch (e) { toast(e.message); } },
      }))));
  };
  showTags(s.hiddenTags);
  const tagInput = el('input', { id: 'hide-tag', autocapitalize: 'none', spellcheck: false, placeholder: 'e.g. feet' });
  const tagForm = el('form', {
    className: 'row',
    onsubmit: async e => {
      e.preventDefault();
      const tag = tagInput.value.trim();
      if (!tag) return;
      try {
        showTags((await queue(() => api('POST', '/api/hidden-tags', { tag }))).hiddenTags); tagInput.value = '';
      } catch (err) { toast(err.message); }
    },
  }, el('label', { htmlFor: 'hide-tag', text: 'Hide a tag' }), tagInput, el('button', { type: 'submit', text: 'Hide' }));

  const admin = state.user.role === 'admin' && el('div', { className: 'links' },
    el('a', { className: 'item', href: '/members', text: 'Members' }),
    el('a', { className: 'item', href: '/reports' }, 'Reported posts',
      state.waitingReports > 0 && el('b', { className: 'count', text: ` ${state.waitingReports}` })));

  app.append(el('section', { className: 'page' },
    el('h1', { text: 'Settings' }),
    el('p', { className: 'muted', text: `Signed in as ${state.user.username}` }),
    sitesBox, mediaBox,
    el('fieldset', {}, el('legend', { text: 'Hidden tags' }), tagForm, list),
    admin,
    el('button', {
      className: 'signout', type: 'button', text: 'Sign out',
      onclick: async () => {
        try { await api('POST', '/api/logout', {}); } catch { /* signed out anyway */ }
        state = await api('GET', '/api/state').catch(() => ({ user: null }));
        navigate('/login', true);
      },
    }),
    el('p', { className: 'muted app-version', text: `borotok ${APP_VERSION}` })));
}

// ---------- members ----------
function linkBox(link) {
  const input = el('input', { className: 'link', readOnly: true, value: link, 'aria-label': 'Link', onfocus: e => e.target.select() });
  return el('div', { className: 'linkbox' }, input,
    el('button', {
      type: 'button', text: 'Copy',
      onclick: async () => { try { await navigator.clipboard.writeText(link); toast('Copied'); } catch { input.select(); } },
    }));
}

async function membersScreen() {
  const links = el('div', { className: 'new-link' });
  const list = el('ul', { className: 'members' });
  app.append(el('section', { className: 'page' },
    el('h1', { text: 'Members' }),
    el('button', {
      className: 'primary', type: 'button', text: 'New invite link',
      onclick: async () => { try { links.replaceChildren(el('p', { className: 'muted', text: 'Invite link - works once:' }), linkBox((await api('POST', '/api/invites', {})).link)); } catch (e) { toast(e.message); } },
    }),
    links, list));
  let members = [];
  try { members = await api('GET', '/api/members'); } catch (e) { return toast(e.message); }
  for (const m of members) {
    const extra = el('div', { className: 'member-link' });
    const li = el('li', { className: 'member', 'data-username': m.username },
      el('div', { className: 'who' }, el('span', { text: m.username }), m.role === 'admin' && el('span', { className: 'muted', text: ' admin' })),
      el('div', { className: 'acts' },
        el('button', {
          type: 'button', text: 'Reset link',
          onclick: async () => { try { extra.replaceChildren(linkBox((await api('POST', `/api/members/${m.id}/reset`, {})).link)); } catch (e) { toast(e.message); } },
        }),
        m.username !== state.user.username && el('button', {
          type: 'button', className: 'danger', text: 'Remove',
          onclick: async () => {
            if (!confirm(`Remove ${m.username}? They are signed out at once.`)) return;
            try { await api('DELETE', `/api/members/${m.id}`, {}); li.remove(); } catch (e) { toast(e.message); }
          },
        })),
      extra);
    list.append(li);
  }
}

// ---------- boot ----------
(async () => {
  try { await loadState(); } catch { state = { user: null }; }
  for (const a of nav.querySelectorAll('a[data-icon]')) a.prepend(icon(a.dataset.icon));
  render();
  startInstallPrompt({ el });
})();
